/**
 * Plans API Service
 *
 * Handles all API calls for admin plan management.
 */

import type {
  Plan,
  PlansListResponse,
  CreatePlanRequest,
  UpdatePlanRequest,
  PlanFeature,
  ToggleFeatureRequest,
  ShopifyValidationRequest,
  ShopifyValidationResponse,
} from '../types/plans';
import { createHeaders } from './apiClient';
import { ApiError, handleResponse } from './httpResponse';

// VITE_API_URL || '' + explicit /api/... paths on every call below —
// matching every other service (apiClient.ts, connectorsApi.ts,
// entitlementsApi.ts, ...). The previous `|| '/api'` default combined with
// unprefixed paths ('/api/admin/plans') only worked by accident when
// VITE_API_URL was unset; with it set (a deployed API on its own origin)
// every request here silently dropped the /api prefix and 404'd.
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  // Shared token source (Frontegg session first, legacy localStorage
  // fallback). This file was the last active service reading only the
  // legacy 'auth_token' key, so hosted-login sessions sent
  // unauthenticated /api/admin/plans requests (PR #10 review finding).
  const headers: HeadersInit = {
    ...createHeaders(),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    // Always throws — delegating the parse keeps this file off its own
    // copy of the detail/detail.message rules. The `HTTP <status>`
    // fallback is preserved verbatim: AdminPlans.tsx renders it.
    //
    // Note the two-layer shape this creates: because this throws on every
    // non-ok response, the per-method `handleResponse<T>(response)` calls
    // below only ever see ok responses, and act purely as the JSON/empty
    // body reader. Their error branch is unreachable by construction.
    await handleResponse(response, `HTTP ${response.status}`);
  }

  return response;
}

export const plansApi = {
  /**
   * List all plans with optional pagination and filters
   */
  async listPlans(params?: {
    include_inactive?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<PlansListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.include_inactive) {
      searchParams.set('include_inactive', 'true');
    }
    if (params?.limit) {
      searchParams.set('limit', params.limit.toString());
    }
    if (params?.offset) {
      searchParams.set('offset', params.offset.toString());
    }

    const queryString = searchParams.toString();
    const url = `/api/admin/plans${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url);
    return handleResponse<PlansListResponse>(response);
  },

  /**
   * Get a specific plan by ID
   */
  async getPlan(planId: string): Promise<Plan> {
    const response = await fetchWithAuth(`/api/admin/plans/${planId}`);
    return handleResponse<Plan>(response);
  },

  /**
   * Create a new plan
   */
  async createPlan(data: CreatePlanRequest): Promise<Plan> {
    const response = await fetchWithAuth('/api/admin/plans', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleResponse<Plan>(response);
  },

  /**
   * Update an existing plan
   */
  async updatePlan(planId: string, data: UpdatePlanRequest): Promise<Plan> {
    const response = await fetchWithAuth(`/api/admin/plans/${planId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return handleResponse<Plan>(response);
  },

  /**
   * Toggle a feature on/off for a plan
   */
  async toggleFeature(
    planId: string,
    data: ToggleFeatureRequest
  ): Promise<PlanFeature> {
    const response = await fetchWithAuth(`/api/admin/plans/${planId}/features/toggle`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleResponse<PlanFeature>(response);
  },

  /**
   * Delete a plan (use with caution - prefer deactivating)
   */
  async deletePlan(planId: string): Promise<void> {
    await fetchWithAuth(`/api/admin/plans/${planId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Validate Shopify plan sync
   */
  async validateShopifySync(
    planId: string,
    data: ShopifyValidationRequest
  ): Promise<ShopifyValidationResponse> {
    const response = await fetchWithAuth(`/api/admin/plans/${planId}/validate-shopify`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleResponse<ShopifyValidationResponse>(response);
  },
};

/** Re-exported so `import { ApiError } from '../services/plansApi'`
 * keeps working; it is now the one shared class, not a private twin. */
export { ApiError };
export default plansApi;
